/**
 * Catastrophic Plan Data Mapper
 * 
 * Maps PlanBuilder responses (using options.key from YAML) to the nested
 * structure expected by generateCatastrophicPlanDocument().
 * 
 * YAML keys → Generator formData structure:
 * - ownerName → businessInfo.ownerName
 * - hasSuccessor → successorInfo.hasSuccessor (converted to boolean)
 * - includeDeath → triggerEvents.includeDeath (converted to boolean)
 * - etc.
 */

/**
 * Map a yes/no string value to a boolean
 */
const toBoolean = (value) => {
  if (typeof value === 'boolean') return value;
  return value === 'yes' || value === true;
};

/**
 * Build answer map from progress for quick lookup
 */
const buildAnswerMap = (progress) => {
  const answerMap = {};
  const questionKeyMap = {};
  
  // First pass: map question IDs to their options.key
  progress?.planTemplate?.planModules?.forEach(module => {
    module.planSections?.forEach(section => {
      section.planQuestions?.forEach(question => {
        const key = question.options?.key;
        if (key) {
          questionKeyMap[question.id] = key;
        }
      });
    });
  });
  
  // Second pass: map keys to answer values
  progress?.userPlanResponses?.forEach(response => {
    const key = questionKeyMap[response.planQuestionId];
    if (key) {
      answerMap[key] = response.answer?.value;
    }
  });
  
  return answerMap;
};

/**
 * Maps PlanBuilder progress to the formData structure expected by
 * generateCatastrophicPlanDocument()
 * 
 * @param {Object} progress - The userPlanProgress from GraphQL
 * @returns {Object} formData - Structured data for the generator
 */
export const mapPlanBuilderToCatastrophicFormat = (progress) => {
  const answers = buildAnswerMap(progress);
  
  const formData = {
    // Business Information
    businessInfo: {
      ownerName: answers.ownerName || '',
      ownerAddress: answers.ownerAddress || '',
      businessName: answers.businessName || '',
      businessType: answers.businessType || '',
      yearEstablished: answers.yearEstablished || '',
      aum: answers.aum || '',
      annualRevenue: answers.annualRevenue || ''
    },
    
    // Successor Information
    successorInfo: {
      hasSuccessor: toBoolean(answers.hasSuccessor),
      successorName: answers.successorName || '',
      successorAddress: answers.successorAddress || '',
      successorMarketplace: toBoolean(answers.successorMarketplace)
    },
    
    // Trigger Events
    triggerEvents: {
      includeDeath: toBoolean(answers.includeDeath),
      includeDisability: toBoolean(answers.includeDisability),
      disabilityDefinition: answers.disabilityDefinition || '',
      includeRetirement: toBoolean(answers.includeRetirement),
      retirementDefinition: answers.retirementDefinition || '',
      includeSale: toBoolean(answers.includeSale),
      otherTriggerEvents: answers.otherTriggerEvents || ''
    },
    
    // Valuation Methodology
    valuationMethodology: {
      valuationMethod: answers.valuationMethod || '',
      revenueMultiple: answers.revenueMultiple || '',
      earningsMultiple: answers.earningsMultiple || '',
      aumPercentage: answers.aumPercentage || '',
      bookValuePremium: answers.bookValuePremium || '',
      appraisalMethod: answers.appraisalMethod || '',
      agreedValue: answers.agreedValue || '',
      valueUpdateFrequency: answers.valueUpdateFrequency || '',
      customFormula: answers.customFormula || '',
      includeTangibleAssets: toBoolean(answers.includeTangibleAssets),
      includeAccounts: toBoolean(answers.includeAccounts),
      includeBrandValue: toBoolean(answers.includeBrandValue),
      excludedAssets: answers.excludedAssets || ''
    },
    
    // Payment Terms
    paymentTerms: {
      paymentStructure: answers.paymentStructure || '',
      downPaymentPercentage: answers.downPaymentPercentage || '',
      installmentPeriod: answers.installmentPeriod || '',
      installmentFrequency: answers.installmentFrequency || '',
      interestRate: answers.interestRate || '',
      earnOutPeriod: answers.earnOutPeriod || '',
      earnOutMetric: answers.earnOutMetric || '',
      securitization: answers.securitization || '',
      securityDetails: answers.securityDetails || ''
    },
    
    // Client Transition
    clientTransition: {
      transitionPeriod: answers.transitionPeriod || '',
      ownerInvolvement: answers.ownerInvolvement || '',
      ownerCompensation: answers.ownerCompensation || '',
      compensationDetails: answers.compensationDetails || '',
      clientNotification: answers.clientNotification || '',
      clientApproval: toBoolean(answers.clientApproval),
      retentionIncentives: answers.retentionIncentives || ''
    },
    
    // Legal & Regulatory
    legalRegulatory: {
      governingLaw: answers.governingLaw || '',
      regulatoryApprovals: answers.regulatoryApprovals || [],
      otherApprovals: answers.otherApprovals || '',
      nonCompete: toBoolean(answers.nonCompete),
      nonCompeteDuration: answers.nonCompeteDuration || '',
      nonCompeteGeography: answers.nonCompeteGeography || '',
      disputeResolution: answers.disputeResolution || ''
    },
    
    // Right of First Refusal
    rightOfFirstRefusal: {
      includeROFR: toBoolean(answers.includeROFR),
      rofrNegotiationPeriod: answers.rofrNegotiationPeriod || '',
      rofrMatchPeriod: answers.rofrMatchPeriod || ''
    },
    
    // Additional Provisions
    additionalProvisions: {
      keyPersonInsurance: toBoolean(answers.keyPersonInsurance),
      insuranceAmount: answers.insuranceAmount || '',
      clientAccessRestrictions: answers.clientAccessRestrictions || '',
      intellectualProperty: answers.intellectualProperty || '',
      otherProvisions: answers.otherProvisions || ''
    },
    
    // Catastrophic Partner (if using marketplace)
    catastrophicPartner: {
      // This would be populated from marketplace selection if applicable
    }
  };
  
  return formData;
};

export default mapPlanBuilderToCatastrophicFormat;


